// src/utils/errorReporter.js
// Best-effort capture of creator-facing issues (API errors, network/timeouts,
// and uncaught client errors). Reports are POSTed to the backend and surfaced
// in the Admin panel. Reporting is fire-and-forget and must never throw or
// cause a user-visible error itself.
import axios from 'axios';
import { auth } from '../firebase/config';
import { API_BASE_URL } from '../config';

const REPORT_PATH = '/api/creators/me/error-log';

// Tracks in-progress file uploads (asset/thumbnail/screenshots, and any other
// surface that opts in) across the app. The stale-chunk auto-reload below is a
// reasonable recovery for "you're on an old deploy" — but forcing it while a
// multi-minute ZIP/product-file upload is mid-flight throws away the
// creator's form and progress for a problem they didn't cause. Large/slow
// uploads are exactly the case most likely to overlap a deploy window, which
// is why this showed up specifically on ZIP/product-file/thumbnail uploads.
let activeUploads = 0;
export function beginUpload() { activeUploads += 1; }
export function endUpload() { activeUploads = Math.max(0, activeUploads - 1); }
// Collapse identical errors within this window. Background polling (e.g. the
// notification unread-count check) retries every 30s, so a short window here
// used to produce a fresh "server error" report for every single retry during
// any outage or mobile network blip — flooding the creator issue log with
// duplicates of the same problem instead of surfacing it once.
const DEDUPE_MS = 5 * 60 * 1000;
const recent = new Map(); // key -> last-sent timestamp

function shouldThrottle(key) {
  const now = Date.now();
  if (recent.size > 300) recent.clear(); // crude cap to avoid unbounded growth
  const last = recent.get(key);
  if (last && now - last < DEDUPE_MS) return true;
  recent.set(key, now);
  return false;
}

const BOT_RE = /bot|crawl|spider|slurp|bingbot|googlebot|duckduckbot|baiduspider|yandex|facebookexternalhit|embedly|quora|pinterest|slackbot|vkshare|whatsapp|telegram|discordbot|headless|lighthouse|preview|scanner/i;

// Crawlers/bots aren't creators — their errors (especially failed chunk loads)
// are noise and must not appear in the creator issue log.
function isBot() {
  try {
    return typeof navigator !== 'undefined' && BOT_RE.test(navigator.userAgent || '');
  } catch (_) {
    return false;
  }
}

// A failed lazy-chunk load almost always means the user is on a stale
// index.html after a deploy (the old hashed filenames were purged). Reloading
// once pulls the fresh index + chunks. Guarded via sessionStorage so it can
// never loop.
//
// These patterns are UNAMBIGUOUS — nothing else in the app produces them.
const CHUNK_RE = /ChunkLoadError|Loading chunk\s+\S+\s+failed|Loading CSS chunk/i;

// These are NOT. They are ordinary SyntaxError text, and they are also exactly
// what JSON.parse throws when it is handed an HTML error page — or what an XML
// document produces. A presigned S3 upload that is rejected (file too large,
// policy expired) answers with XML; an API Gateway or CDN failure answers with
// HTML. Either one produces "Unexpected token '<'".
//
// This used to sit in the same list as the patterns above, so ANY upload that
// failed that way reloaded the page out from under the creator — losing every
// field they had filled in, with no error shown. That is the reverse of what
// this function is for: it exists to recover a page that is already broken,
// not to destroy a working one.
//
// They are still worth matching, because a stale client asking for a deleted
// chunk really is served index.html and really does throw exactly this. The
// difference is provenance, so they only count when the browser tells us the
// failure came from a script or stylesheet — which is true for a chunk load
// and never true for a parsed API response.
const AMBIGUOUS_PARSE_RE = /Unexpected token '<'|Unexpected end of input/i;

const STATIC_ASSET_RE = /\/static\/(js|css)\/|\.chunk\.js/i;

/**
 * @param {string} message
 * @param {object} [ctx]
 * @param {string} [ctx.source] filename/URL the browser blamed, when it gave one.
 *   Absent for promise rejections, which is precisely why the ambiguous
 *   patterns must not fire on them.
 */
function maybeRecoverChunkError(message, ctx = {}) {
  const msg = message || '';
  const definitelyChunk = CHUNK_RE.test(msg);
  const maybeChunk = AMBIGUOUS_PARSE_RE.test(msg) && STATIC_ASSET_RE.test(ctx.source || '');
  if (!definitelyChunk && !maybeChunk) return false;

  // Never blow away an in-progress upload — report normally and let recovery
  // happen on the next navigation instead of reloading out from under it.
  if (activeUploads > 0) return false;
  try {
    const KEY = '__mintlink_chunk_reload_at';
    const last = Number(sessionStorage.getItem(KEY) || 0);
    if (Date.now() - last > 30000) {
      sessionStorage.setItem(KEY, String(Date.now()));
      window.location.reload();
      return true;
    }
  } catch (_) { /* ignore */ }
  return false;
}

// Environmental/third-party noise that isn't an actionable app bug:
//  - IndexedDB "full disk" / quota errors — the user's device is out of storage;
//    nothing in this codebase can fix that (surfaces from Firebase Auth's own
//    IndexedDB-backed persistence, which we don't control).
//  - Bare "AbortError" — a fetch/media/play() promise aborted by navigation or
//    a component unmount racing its own request; routine, not a defect.
//  - window.webkit.messageHandlers / sendDataToNative / dispatchToBridge — these
//    function names don't exist anywhere in this codebase; they're thrown by
//    the Facebook/Instagram in-app browser's own injected instrumentation
//    script running on the page, not by app code.
const NOISE_RE = /indexedDB\.open|QuotaExceededError|full disk|^AbortError$|webkit\.messageHandlers|sendDataToNative|dispatchToBridge|setupIosCallbackHandler|logLoginFieldDetected/i;

function isNoise(message, detail) {
  return NOISE_RE.test(message || '') || NOISE_RE.test(detail || '');
}

async function postReport(body) {
  try {
    let token = null;
    try {
      token = auth?.currentUser ? await auth.currentUser.getIdToken() : null;
    } catch (_) { /* ignore token errors */ }
    // Use a bare axios (not the app instance) so this request is never itself
    // intercepted / reported, avoiding recursion.
    await axios.post(`${API_BASE_URL}${REPORT_PATH}`, body, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 8000,
    });
  } catch (_) {
    // Swallow — reporting failures must stay invisible to the creator.
  }
}

// Categorize + report an axios error. Only "real problems" are kept:
// server errors (5xx), auth (401), permission (403), and network/timeouts.
export function reportApiError(error) {
  try {
    if (isBot()) return;
    const cfg = error?.config || {};
    const url = cfg.url || '';
    if (typeof url === 'string' && url.includes(REPORT_PATH)) return; // never report the reporter

    const resp = error?.response;
    let category;
    let statusCode = null;

    if (!resp) {
      category = error?.code === 'ECONNABORTED' ? 'timeout' : 'network';
    } else {
      statusCode = resp.status;
      // Skip benign admin-probe 403s: the app detects whether a user is an admin
      // by calling an admin-only endpoint (/api/admin/rbac/me) and treating a 403
      // as "not an admin". These fire for every non-admin creator on login and are
      // expected — not real issues.
      if (statusCode === 403 && typeof url === 'string' && url.includes('/api/admin/')) return;
      if (statusCode >= 500) category = 'server_error';
      else if (statusCode === 401) category = 'auth';
      else if (statusCode === 403) category = 'permission';
      else return; // routine 4xx (404 / 422 / etc.) — intentionally skipped
    }

    const method = (cfg.method || '').toUpperCase();
    const key = `api|${category}|${method}|${url}|${statusCode}`;
    if (shouldThrottle(key)) return;

    let detail = '';
    try {
      detail = typeof resp?.data === 'string' ? resp.data : JSON.stringify(resp?.data);
    } catch (_) { /* ignore serialization errors */ }

    postReport({
      source: 'frontend',
      category,
      status_code: statusCode,
      method,
      endpoint: url,
      message: error?.message || 'Request failed',
      detail: (detail || '').slice(0, 2000),
      page_url: typeof window !== 'undefined' ? window.location.href : null,
    });
  } catch (_) { /* never throw from the reporter */ }
}

// Report an uncaught client-side error (JS crash / unhandled rejection).
export function reportClientError(message, detail) {
  try {
    if (isBot()) return;
    if (isNoise(message, detail)) return;
    const key = `client|${message}`;
    if (shouldThrottle(key)) return;
    postReport({
      source: 'frontend',
      category: 'client',
      message: (message || 'Client error').slice(0, 500),
      detail: (detail || '').slice(0, 2000),
      page_url: typeof window !== 'undefined' ? window.location.href : null,
    });
  } catch (_) { /* never throw from the reporter */ }
}

let installed = false;
// Install global handlers for uncaught errors and promise rejections.
export function installGlobalErrorReporting() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e) => {
    // Ignore cross-origin "Script error." events. When an error originates in a
    // script from another origin (browser extensions, in-app browsers like the
    // Instagram/Vivo webview, third-party widgets), the browser strips the
    // message, stack and location for security — leaving nothing actionable.
    // These have no `error` object and no `filename`, so skip them as noise.
    if (!e?.error && !e?.filename) return;
    if (isBot()) return;
    const msg = e?.message || 'Uncaught error';
    // Stale-deploy chunk failure → reload once instead of logging a dead-end.
    // e.filename is the resource the browser blamed — a chunk URL for a real
    // stale-deploy failure, and the page's own URL for anything else.
    if (maybeRecoverChunkError(msg, { source: e?.filename })
        || maybeRecoverChunkError(e?.error?.name, { source: e?.filename })) return;
    reportClientError(
      msg,
      e?.error?.stack || `${e?.filename || ''}:${e?.lineno || ''}:${e?.colno || ''}`,
    );
  });
  window.addEventListener('unhandledrejection', (e) => {
    if (isBot()) return;
    const reason = e?.reason;
    const msg = reason?.message || 'Unhandled promise rejection';
    // No source: a rejected promise carries no blamed resource, so only the
    // unambiguous ChunkLoadError patterns can trigger a reload here. A failed
    // upload parsing an XML/HTML error body must never reload the page.
    if (maybeRecoverChunkError(msg) || maybeRecoverChunkError(reason?.name)) return;
    reportClientError(msg, reason?.stack || String(reason));
  });
}
